import { ReactNode } from 'react';
import { SearchIcon, FilterIcon, CloseIcon } from '@/components/icons';
import { Loader } from '@/components/ui/Loader';

export interface DataTableColumn<T> {
  key: string;
  label: string;
  render?: (item: T) => ReactNode;
  className?: string;
}

interface DataTableProps<T> {
  data: T[];
  columns: DataTableColumn<T>[];
  onRowClick?: (item: T) => void;
  renderRow?: (item: T) => ReactNode;
  searchable?: boolean;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  filterable?: boolean;
  onFilterClick?: () => void;
  loading?: boolean;
  loadingMessage?: string;
  emptyTitle?: string;
  emptyMessage?: string;
  emptyIcon?: ReactNode;
  emptyAction?: ReactNode;
  error?: Error | null;
  errorTitle?: string;
  className?: string;
}

export function DataTable<T extends { [key: string]: any }>({
  data,
  columns,
  onRowClick,
  renderRow,
  searchable = true,
  searchValue = '',
  onSearchChange,
  searchPlaceholder = 'Search',
  filterable = true,
  onFilterClick,
  loading = false,
  loadingMessage = 'Loading...',
  emptyTitle = 'No Data',
  emptyMessage = 'No items to display',
  emptyIcon,
  emptyAction,
  error = null,
  errorTitle = 'Error Loading Data',
  className = ''
}: DataTableProps<T>) {


  if (loading) {
    return (
      <div className={className}>
        <Loader size="md" variant="wine" message={loadingMessage} />
      </div>
    );
  }

 
  if (error) {
    return (
      <div className={`text-center py-20 ${className}`}>
        <div className="inline-block bg-red-100 p-4 rounded-full">
          <svg className="w-10 h-10 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="mt-4 font-semibold text-gray-800 text-lg">{errorTitle}</h3>
        <p className="mt-1 text-sm text-gray-500">{error.message || 'Something went wrong'}</p>
      </div>
    );
  }

  return (
    <div className={className}>
      {/* Search and Filter Bar */}
      {(searchable || filterable) && (
        <div className="py-4 sm:py-5 flex justify-between items-center">
          {searchable && (
            <div className="relative max-w-xs w-full">
              <label htmlFor="search-input" className="sr-only">Search</label>
              <input
                type="text"
                id="search-input"
                placeholder={searchPlaceholder}
                value={searchValue}
                onChange={(e) => onSearchChange?.(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    onSearchChange?.(searchValue);
                  }
                }}
                className="w-full pl-4 pr-10 py-2 border border-gray-300 rounded-lg text-sm focus:ring-0  focus:border-none focus:outline-none bg-[#FAFAFA]"
              />
              {searchValue ? (
                <button
                  type="button"
                  onClick={() => onSearchChange?.('')}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 hover:opacity-70 transition-opacity"
                  aria-label="Clear search"
                >
                  <CloseIcon className="w-4 h-4 text-gray-600 cursor-pointer" />
                </button>
              ) : (
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <SearchIcon className="w-4 h-4 text-gray-400" />
                </div>
              )}
            </div>
          )}
          {filterable && (
            <button
              onClick={onFilterClick}
              className="flex items-center gap-2 text-sm font-semibold text-forestGreen-100 bg-white border border-gray-300 rounded-lg px-4 py-2 hover:bg-gray-50 transition-colors ml-4"
            >
              <FilterIcon className="w-4 h-4" />
              Filter
            </button>
          )}
        </div>
      )}

      {/* Empty State */}
      {data.length === 0 ? (
        <div className="text-center py-20">
          <div className="inline-block bg-gray-200/20 p-4 rounded-full">
            {emptyIcon || (
              <img src="/No-post-icon.png" alt="No data" className="w-20 h-20 object-contain" />
            )}
          </div>
          <h3 className="mt-4 font-semibold text-gray-800 text-lg">{emptyTitle}</h3>
          <p className="mt-1 text-sm text-gray-500">{emptyMessage}</p>
          {emptyAction && (
            <div className="mt-6">
              {emptyAction}
            </div>
          )}
        </div>
      ) : (
       
        <div className="space-y-2">
          {data.map((item, index) => (
            <div key={index}>
              {renderRow ? renderRow(item) : (
                <div
                  onClick={() => onRowClick?.(item)}
                  className={`p-4 border border-gray-200 rounded-lg ${
                    onRowClick ? 'cursor-pointer hover:bg-gray-50' : ''
                  }`}
                >
              
                  <div className="grid grid-cols-1 gap-2">
                    {columns.map((column) => (
                      <div key={column.key} className={column.className}>
                        <span className="font-semibold text-xs text-gray-500">{column.label}: </span>
                        <span className="text-sm text-gray-800">
                          {column.render ? column.render(item) : item[column.key]}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default DataTable;
